import { chatKindOf } from '@bt-common/schemas/wa/chat-kind';
import { isWhatsappExtensionContext } from "@common-services/detect-context";
import { WhatsappToolsClientActions } from "./wa-tools-client-actions";
import { isWireError } from "@common/services/post-message-to-whatsapp-functions";
import { triggerRequesthWhatsappTool } from "./wa-tools-server-actions";
import { WA_TOOL_COMMANDS, waToolError } from "../../../bt-common/interfaces/whatsapp-tools-commands.interface";
import type { WaToolResponse } from "../../../bt-common/interfaces/whatsapp-tools-commands.interface";
import type { ValidateChatIdParams, ValidateChatIdResult } from "./wa-tools-server-actions";

interface ValidateChatIdForToolOptions {
    requireGroup?: boolean;
    /**
     * When true, also verify the chatId/phone is registered on WhatsApp via
     * `waValidatePhoneNumberOrChatIdTool` — a network round-trip to the engine.
     *
     * Default is FALSE: tools that operate on an existing chat (read-only
     * lookups, chat-state mutations, group ops) let the inner engine operation
     * fail for a missing chat, so this pre-flight is pure overhead and fires a
     * spurious `waValidatePhoneNumberOrChatIdTool` request the caller never
     * asked for. Only opt in where existence must be checked up-front before
     * acting — e.g. the preemptive send/schedule validation that marks a
     * message `failed` with a clean "not on WhatsApp" reason before delivery.
     */
    validateExistence?: boolean;
}

/**
 * Shared chatId validation for all WhatsApp tools.
 * 1. Checks chatId is present (not empty/whitespace)
 * 2. If requireGroup is true, checks chatId is a group ID (contains @g.us)
 * 3. Normalizes raw phone numbers to `<digits>@c.us`
 * 4. If validateExistence is true, validates the chatId is on WhatsApp via the
 *    validation tool (extension or server mode) — opt-in, see the option doc.
 *
 * Returns a WaToolResponse error if invalid, or null if valid.
 */
export const validateChatIdForTool = async (
    chatId: string | undefined | null,
    options?: ValidateChatIdForToolOptions,
): Promise<WaToolResponse<never> | null> => {
    const trimmed = String(chatId || '').trim();
    if (!trimmed) {
        return waToolError('invalid_input', 'chatId is required');
    }

    if (options?.requireGroup && chatKindOf(trimmed) !== 'group') {
        return waToolError('not_a_group', 'chatId must be a group chat ID (ending with @g.us)');
    }

    // Normalize phone numbers (no '@' means it's a raw phone number)
    const isPhoneNumber = !trimmed.includes('@');
    let resolvedChatId = trimmed;
    if (isPhoneNumber) {
        const cleanPhone = trimmed.replace(/[^\d]/g, '');
        if (!cleanPhone) {
            return waToolError('invalid_input', 'Phone number contains no valid digits');
        }
        resolvedChatId = `${cleanPhone}@c.us`;
    }

    // Existence check is opt-in: by default we skip the network round-trip to
    // waValidatePhoneNumberOrChatIdTool so it only runs when explicitly needed.
    if (!options?.validateExistence) {
        return null;
    }

    const notOnWhatsApp = () =>
        waToolError('not_on_whatsapp', `${isPhoneNumber ? 'Phone number' : 'Chat ID'} is not on WhatsApp`);

    if (isWhatsappExtensionContext()) {
        const result = await WhatsappToolsClientActions.waValidatePhoneNumberOrChatIdTool(
            resolvedChatId,
            { useForceUsync: false },
        );
        if (!result || isWireError(result) || !result.valid) {
            return notOnWhatsApp();
        }
    } else {
        const result = await triggerRequesthWhatsappTool<ValidateChatIdParams, ValidateChatIdResult>(WA_TOOL_COMMANDS.waValidatePhoneNumberOrChatIdTool,
            { chatId: resolvedChatId, options: { useForceUsync: false } },
        );
        if (!result.success) return notOnWhatsApp();
        if (!(result as any).data?.valid) return notOnWhatsApp();
    }

    return null;
};
